/**
 * Romanian copy — the reference dictionary.
 *
 * This file is the source of truth for the `Dictionary` shape. A second locale
 * is added by copying this file, translating the values, and registering it in
 * `./index.ts`. TypeScript will then flag any key the new locale forgets.
 *
 * Rules for adding copy here:
 *  - No string concatenation at the call site. If a phrase needs a value
 *    interpolated, expose it as a function (see `fileTooLarge`). Word order
 *    differs between languages; concatenation bakes in Romanian word order.
 *  - No copy in components. If you are about to type a user-facing string in a
 *    .tsx file, it belongs here instead.
 *
 * Voice — Copy v2, as written by Mario-Dan. The audience is the PASSIVE owner:
 * someone who inherited the plot or bought it years ago, is sceptical, and has
 * no active intention to sell.
 *  - Formal address ("dumneavoastră") throughout.
 *  - Register is split on purpose: the impersonal reflexive for PROCESS
 *    ("parcela se localizează", "detaliile se completează") and the first
 *    person plural for RELATIONSHIP ("vă spunem direct", "clarificăm
 *    împreună"). A firm states what happens; people answer for it.
 *  - NO EM DASH anywhere in user-facing copy. The en dash survives only inside
 *    numeric ranges ("3–5 zile"). Where an em dash used to join clauses, the
 *    copy now breaks the sentence instead.
 *  - The imperative survives at the point of action (buttons, the hint under
 *    the map, validation). "Desenați parcela" is a direction, not a pitch.
 *  - No intention is required of the owner. Copy that presumed a sale in
 *    progress ("Intenția dumneavoastră") was recalibrated to validate simply
 *    owning the land ("Situația dumneavoastră", "Nicio intenție nu este
 *    necesară").
 */

export const ro = {
  meta: {
    htmlLang: 'ro',
    // "evaluare gratuită de teren" stays in the title for search. Pipe, not an
    // em dash, per the v2 punctuation rule.
    documentTitle: 'Evaluare gratuită de teren | PRQ Partners',
    metaDescription:
      'Punctajul de dezvoltare al terenului dumneavoastră, pregătit individual de o echipă cu expertiză arhitecturală. Estimare de valoare și potențial de preț. Fără listare, fără intermediere.',
    /**
     * Leaflet-Geoman ships its own translations and defaults to English. Naming
     * its locale code here means a new dictionary switches Geoman's tooltips
     * along with everything else, instead of silently leaking English onto the
     * map. Must be one of Geoman's SupportLocales.
     */
    geomanLang: 'ro',
  },

  brand: {
    name: 'PRQ Partners',
    wordmark: 'PRQ',
    tagline: 'Analiză de teren',
  },

  app: {
    /*
     * The hero, built like the one on prqpartners.com: a display claim, one
     * paragraph under it, then a single glass call to action carrying its own
     * qualifying line. Nothing else — the process detail that used to run on
     * here now lives where it is actually needed, in step 1 and the FAQ.
     */
    // H1 — a single claim, stated once.
    title: 'Valoarea unui teren stă în potențialul de dezvoltare.',
    // Subhead: the owner's situation, then the first step. Two sentences.
    intro:
      'Poate l-ați moștenit, poate l-ați cumpărat cu ani în urmă și vă gândiți la el din când în când. Primul pas este să știți exact ce aveți.',
    /*
     * The CTA. The arrow is NOT part of this string — it is a decorative span
     * in the markup, so a screen reader announces the action and not "right
     * arrow", and a future locale is not forced to carry the glyph.
     */
    ctaLabel: 'Aflați valoarea terenului',
    /*
     * The line under the button, not inside it. It is the same kind of text as
     * a field's help line — the interface speaking ABOUT a control rather than
     * labelling it — so it takes the same italic grey treatment.
     *
     * Agreement: "analiza" is feminine, so "gratuită" and "trimisă", not
     * "gratuit" / "trimisa".
     */
    ctaNote: 'Analiza gratuită, trimisă prin email.',
    stepOf: (current: number, total: number) => `Pasul ${current} din ${total}`,
  },

  /**
   * The assurance block: three answers to the sceptic's three objections —
   * who are you, why is it free, what does NOT happen. Rendered immediately
   * below the fold, before step 1, as a FAQ accordion copying the formatting
   * of the FAQ section on prqpartners.com (heading, hairline rows, plus icon).
   */
  assurance: {
    heading: 'Întrebări frecvente',
    /*
     * Every heading is a question and ends in a question mark. They are read as
     * a list of things the owner is already wondering, and a label without a
     * question mark ("Cine face analiza") reads as a section title instead —
     * which is not what an accordion row is.
     *
     * Ordered as the doubts arrive: who you are, why it is free, what arrives,
     * then the two "am I even eligible" fears, then how far the figures can be
     * trusted, then what happens after sending.
     */
    items: [
      {
        heading: 'Cine face analiza?',
        body: 'PRQ Partners este un studio de consultanță strategică pentru dezvoltări imobiliare. Fiecare analiză este pregătită individual, de o echipă cu expertiză arhitecturală.',
      },
      {
        /*
         * States the business model plainly: the analysis is free because the
         * market understanding it builds is what PRQ sells to developers. An
         * owner who works that out for himself after consenting feels sold; one
         * who is told before consenting is being levelled with.
         */
        heading: 'De ce este gratuită?',
        body: 'Analizele de teren sunt modul în care ne construim înțelegerea pieței, parcelă cu parcelă. Această înțelegere este valoarea muncii noastre în relația cu dezvoltatorii cu care lucrăm. De la dumneavoastră nu cerem nimic.',
      },
      {
        heading: 'Ce primesc, mai exact?',
        body: 'Punctajul de dezvoltare al terenului, de la 0 la 100, calculat pe cinci criterii: regim urbanistic, poziție și piață, caracteristicile parcelei, infrastructură și situație juridică. Alături de punctaj, o estimare a valorii actuale a terenului. Pentru terenurile care se califică, analiza include și potențialul de preț: cât ar putea valora terenul dezvoltat corect.',
      },
      {
        heading: 'Nu am certificatul de urbanism. Pot trimite solicitarea?',
        body: 'Da. Fără certificat, regimul de construire se estimează din reglementările zonei, iar punctajul rămâne orientativ până la primirea documentului. Multe solicitări încep exact așa.',
      },
      {
        heading: 'Terenul este în coproprietate sau succesiunea nu este finalizată. Pot trimite?',
        body: 'Da. Situația juridică este unul dintre cele cinci criterii, nu o condiție de acces. O succesiune nefinalizată se reflectă în punctaj, iar în document veți vedea limpede ce ar schimba finalizarea ei.',
      },
      {
        heading: 'Cât de exactă este estimarea de valoare?',
        body: 'Estimarea este un interval orientativ, ancorat pe grilele notariale și pe nivelurile actuale ale pieței, cu logica de calcul prezentată în document. Nu este un raport de evaluare în sensul standardelor ANEVAR și nu poate înlocui unul acolo unde legea îl cere.',
      },
      {
        heading: 'Ce înseamnă că un teren „se califică” pentru analiza completă?',
        body: 'Terenurile cu punctaj de la 65 în sus primesc analiza completă, inclusiv potențialul de preț calculat în logica unui dezvoltator. Sub acest prag, potențialul de preț ar fi o cifră fără acoperire, iar noi nu publicăm cifre fără acoperire. Primiți în schimb punctajul, valoarea actuală și motivele, spuse direct.',
      },
      {
        heading: 'Mă va suna cineva după ce trimit?',
        body: 'Nu. Documentația ajunge prin email. Telefonul este folosit doar dacă avem nevoie de o lămurire despre teren, niciodată pentru vânzare.',
      },
      {
        /*
         * The mechanism behind the ask-first promise, stated concretely. It is
         * the question the "De ce este gratuită?" answer provokes, so it is
         * answered on the same page rather than left to be inferred.
         */
        heading: 'Ce se întâmplă dacă un dezvoltator este interesat de zona terenului meu?',
        body: 'Vă scriem și vă întrebăm dacă doriți o discuție. Dumneavoastră decideți. Fără acordul dumneavoastră, terenul nu este prezentat nimănui, iar datele lui nu părăsesc PRQ. Practic, ținem piața sub observație în locul dumneavoastră.',
      },
      {
        /* "exclusiv" is gone: the conditional contact is a second purpose, and
           claiming a single one here would contradict the consent checkbox. */
        heading: 'Ce se întâmplă cu datele și documentele mele?',
        body: 'Sunt folosite pentru analiza terenului și pentru a vă contacta, inclusiv atunci când există interes de dezvoltare pentru zona dumneavoastră. Sunt stocate pe infrastructura noastră și sunt șterse după maximum 24 de luni sau oricând ne cereți. Nu sunt transmise nimănui fără acordul dumneavoastră. Detaliile complete sunt în politica de confidențialitate.',
      },
    ],
  },

  steps: {
    locate: 'Localizare',
    details: 'Detalii parcelă',
    contact: 'Contact',
  },

  nav: {
    back: 'Înapoi',
    continue: 'Continuați',
    submit: 'Trimiteți solicitarea',
  },

  step1: {
    heading: 'Localizarea parcelei',
    lede: 'Parcela se caută după localitate, apoi conturul se desenează pe hartă. Suprafața se calculează automat.',
    searchLabel: 'Căutați localitatea sau adresa',
    searchPlaceholder: 'ex. Otopeni, Ilfov',
    searchHint: 'Căutarea este limitată la România.',
    searching: 'Se caută…',
    searchNoResults: 'Niciun rezultat. Încercați alt termen sau desenați direct pe hartă.',
    searchError: 'Căutarea nu a funcționat. Verificați conexiunea și încercați din nou.',
    searchClear: 'Ștergeți căutarea',

    drawStart: 'Desenați parcela',
    drawStop: 'Opriți desenarea',
    drawReset: 'Începeți din nou',
    /*
     * "Apăsați" and "marcați", not "atingeți": the same words are read on a
     * phone and on a desktop, and "atingeți" only makes sense to one of them.
     */
    drawHintIdle: 'Apăsați „Desenați parcela”, apoi marcați pe hartă colțurile terenului.',
    drawHintDrawing:
      'Marcați pe rând fiecare colț. La final, apăsați din nou pe primul punct pentru a închide conturul.',
    // "colțuri", not "puncte", for terminological consistency with the hints.
    drawHintDone: 'Conturul poate fi ajustat trăgând de colțuri.',
    /*
     * The location answers, moved here from step 2. Where the parcel is belongs to the
     * step about where the parcel is; asking again two screens later read as making the
     * owner repeat what they had just pinned on a map. Both are prefilled from the
     * centroid and stay editable, because the reverse-geocode names the commune right
     * far more often than it names the village inside it.
     */
    whereHeading: 'Unde se află terenul',
    whereLede: 'Completate din poziția parcelei. Corectați-le dacă nu sunt exacte.',
    judetLabel: 'Județ',
    judetPlaceholder: 'Alegeți județul',
    localitateLabel: 'Localitate / cartier',
    localitateHelp: 'Satul sau cartierul, nu doar comuna, dacă îl știți. Contează la estimare.',
    localitatePlaceholder: 'ex. Otopeni',

    layerSatellite: 'Satelit',
    layerStreet: 'Străzi',
    layerLabel: 'Fundal hartă',
    zoomIn: 'Măriți',
    zoomOut: 'Micșorați',

    /**
     * Overrides for the tooltips Geoman renders on the map canvas itself. These
     * override its bundled Romanian so the wording matches the hint text under
     * the map rather than sounding like a different product.
     */
    mapTooltips: {
      firstVertex: 'Apăsați pe primul colț al parcelei',
      continueLine: 'Apăsați pe următorul colț',
      finishPoly: 'Apăsați pe primul punct pentru a închide conturul',
    },

    areaLabel: 'Suprafață calculată',
    areaEmpty: 'Nicio parcelă desenată',
    areaSqm: (value: string) => `${value} m²`,
    areaHa: (value: string) => `${value} ha`,

    fallbackToggle: 'Nu pot desena parcela',
    fallbackHeading: 'Introduceți datele manual',
    fallbackLede:
      'Dacă nu reușiți să desenați parcela, introduceți suprafața și localitatea. Terenul va fi identificat împreună cu dumneavoastră.',
    fallbackAreaLabel: 'Suprafața terenului (m²)',
    fallbackAreaPlaceholder: 'ex. 4500',
    fallbackLocalityLabel: 'Adresa terenului',
    fallbackLocalityPlaceholder: 'ex. Strada Fermei 12, Otopeni',
    fallbackCancel: 'Înapoi la hartă',

    gateDraw: 'Desenați parcela pe hartă pentru a continua.',
  },

  step2: {
    heading: 'Detaliile parcelei',
    /*
     * Recalibrated for the passive owner: it does not assume the papers are to
     * hand. "Dacă actele nu sunt la îndemână" validates exactly his situation.
     * "Optional" still comes first — this is the step people abandon, and the
     * reason is almost always that they believe they cannot answer it.
     */
    lede: 'Toate câmpurile sunt opționale. Completați doar ce știți. Dacă actele nu sunt la îndemână, trimiteți solicitarea și așa, iar restul clarificăm împreună.',

    areaLabel: 'Suprafață (m²)',
    areaHelpAuto: 'Calculată automat din conturul desenat. Poate fi corectată dacă știți suprafața exactă din acte.',
    areaHelpManual: 'Suprafața declarată de dumneavoastră.',

    judetLabel: 'Județ',
    judetPlaceholder: 'Alegeți județul',

    localitateLabel: 'Localitate / cartier',
    localitatePlaceholder: 'ex. Otopeni',
    localitateHelpPrefilled: 'Completată automat din poziția parcelei. Poate fi corectată.',

    cutLabel: 'CUT (coeficient de utilizare a terenului)',
    cutHelp: 'Se găsește în certificatul de urbanism. Ex. 1,5 sau 2,4.',
    // Romanian writes decimals with a comma, and the help line above says "1,5".
    cutPlaceholder: 'ex. 1,5',

    potLabel: 'POT (procent de ocupare a terenului)',
    potHelp: 'Se găsește în certificatul de urbanism. Valoare între 0 și 100.',
    potPlaceholder: 'ex. 45',
    potSuffix: '%',

    statutLabel: 'Categoria terenului',
    statutHelp: 'Scrie în extrasul de carte funciară sau în certificatul de urbanism. Este cel mai important detaliu pentru valoare.',
    statutPlaceholder: 'Alegeți categoria',
    statutOptions: {
      intravilan: 'Intravilan (în interiorul localității, se poate construi)',
      extravilan: 'Extravilan (în afara localității)',
      agricol: 'Agricol (arabil, pășune, livadă, tarla)',
      /*
       * Same reasoning as the zoning select: an owner who does not know must have
       * somewhere honest to land. When this is chosen the engine falls back to inferring
       * the category from the address and the size, and widens the range to say so.
       */
      nustiu: 'Nu știu',
    },
    zonareLabel: 'Ce se poate construi',
    zonareHelp: 'Se găsește în certificatul de urbanism, la „zonă funcțională”.',
    zonarePlaceholder: 'Alegeți utilizarea',
    zonareOptions: {
      rezidential: 'Rezidențial',
      mixt: 'Mixt',
      comercial: 'Comercial',
      altul: 'Altul',
      /*
       * For the ICP: many passive owners simply do not know. Without this
       * option the select forces a false answer or an abandon. The value must
       * be accepted by the server allowlist AND the SQLite CHECK constraint —
       * see server/src/validation/payload.ts and storage/sqliteStore.ts.
       */
      nustiu: 'Nu știu',
    },
    zonareAltLabel: 'Precizați utilizarea',
    zonareAltPlaceholder: 'ex. Industrial ușor',

    stareLabel: 'Stare actuală a terenului',
    starePlaceholder: 'Alegeți starea',
    stareOptions: {
      liber: 'Teren liber',
      constructie: 'Construcție existentă',
      partial: 'Parțial construit',
    },

    /*
     * Legal status. The help line is the whole point of asking: an owner mid-
     * succession, or one of four siblings on a title, assumes that disqualifies
     * them and closes the tab. Saying plainly that nothing here blocks the
     * analysis is what keeps that owner in the form — and it is also the single
     * most useful thing the operator can know before the first call.
     */
    situatieLabel: 'Situația juridică a terenului',
    situatieHelp: 'Nicio situație nu blochează analiza.',
    situatiePlaceholder: 'Alegeți situația',
    situatieOptions: {
      unic: 'Un singur proprietar, intabulat',
      coproprietari: 'Mai mulți coproprietari',
      succesiune: 'Succesiune în curs sau nefinalizată',
      sarcini: 'Există sarcini sau neclarități (ipotecă, litigiu, acte vechi)',
      nustiu: 'Nu știu exact',
    },

    /*
     * Infrastructure. The two questions the intake form never used to ask, and
     * the ones that move the score most: utilities and road access. Kept simple
     * for the owner: check what exists, pick the road type.
     */
    utilitatiLabel: 'Utilități disponibile',
    utilitatiHelp: 'La limita terenului sau în imediata apropiere. Bifați ce există.',
    utilitatiOptions: {
      curent: 'Curent electric',
      apa: 'Apă curentă',
      gaz: 'Gaz metan',
      canal: 'Canalizare',
    },

    drumLabel: 'Acces auto la teren',
    drumHelp: 'Tipul drumului până la teren.',
    drumPlaceholder: 'Alegeți accesul',
    drumOptions: {
      asfalt: 'Drum asfaltat sau betonat',
      pietruit: 'Drum pietruit',
      pamant: 'Drum de pământ',
    },

    certificateLabel: 'Certificat de urbanism',
    // Does not assume the document exists.
    certificateHelp:
      'Recomandat, dacă îl aveți: accelerează analiza. PDF, JPG sau PNG, maximum 15 MB.',
    /**
     * Shown on step 3 when no certificate is attached. It must not read as an
     * error: the submission is valid without one. Recalibrated for the ICP —
     * for someone not selling, "it takes longer" is no loss. The message says
     * what WE do in its absence, and normalises the omission.
     */
    certificateMissingHeading: 'Nu ați atașat certificatul de urbanism',
    certificateMissingBody:
      'Solicitarea poate fi trimisă și așa. Fără certificat, regimul de construire se estimează din reglementările zonei, iar analiza rămâne orientativă până la primirea documentului. Multe solicitări încep exact așa.',
    certificateMissingAction: 'Atașez certificatul',
    certificateCta: 'Alegeți fișierul',
    certificateCtaReplace: 'Înlocuiți fișierul',
    certificateDrop: 'sau trageți fișierul aici',

    additionalLabel: 'Documente suplimentare',
    additionalOptional: 'opțional',
    additionalHelp:
      'Extras de carte funciară, plan de situație sau schițe. Orice document ajută analiza. PDF, JPG sau PNG, maximum 15 MB per fișier.',
    additionalCta: 'Adăugați fișiere',

    fileRemove: 'Eliminați',
    fileRemoveNamed: (name: string) => `Eliminați fișierul ${name}`,

    noteLabel: 'Situația dumneavoastră',
    /*
     * "Nicio intenție nu este necesară" explicitly validates the passive owner.
     * No comma before "sau" in the enumeration.
     */
    noteHelp:
      'Spuneți-ne, dacă doriți, cum ați ajuns la acest teren și la ce vă gândiți: să îl vindeți cândva, să dezvoltați pe el sau doar să îl păstrați. Nicio intenție nu este necesară pentru analiză.',
    notePlaceholder: 'ex. Am moștenit terenul și analizez dacă merită vândut acum sau peste câțiva ani.',
  },

  step3: {
    heading: 'Date de contact',
    // Names the deliverable + the channel reassurance: email only, no calls.
    /*
     * States the channel and stops. It used to add "Nu vă sunăm și nu vă
     * contactăm în alt scop", which sat two inches above a consent checkbox
     * granting exactly that other purpose — the sentence denied what the box
     * asked the owner to agree to, which is a consent problem, not a copy one.
     *
     * The no-sales-call promise is not lost: it is on phoneHelp below, attached
     * to the field it actually reassures, and in the FAQ.
     */
    lede: 'Punctajul de dezvoltare este trimis pe adresa de email de mai jos.',

    nameLabel: 'Nume și prenume',
    namePlaceholder: 'ex. Ioana Popescu',

    emailLabel: 'Email',
    emailPlaceholder: 'ex. ioana.popescu@email.ro',

    phoneLabel: 'Telefon',
    phonePlaceholder: 'ex. 0722 123 456',
    /* States what the number is for — and what it is not for. This is the
       field people hesitate over. */
    phoneHelp: 'Folosit doar dacă este nevoie de o lămurire despre teren. Fără apeluri de vânzare.',

    /*
     * The consent now covers the conditional contact — "inclusiv atunci când
     * există interes de dezvoltare pentru zona terenului". That clause is what
     * makes the ask-first promise in the FAQ lawful rather than a surprise:
     * consent has to name the purpose before the purpose happens.
     *
     * Keep this in step with privacy.sections → "Scopul prelucrării". They are
     * the same promise written twice, and only one of them is legally binding
     * if they ever disagree.
     */
    consentText:
      'Sunt de acord ca PRQ Partners să prelucreze datele furnizate (inclusiv documentele încărcate și localizarea parcelei) în scopul analizei terenului și al contactării mele, inclusiv atunci când există interes de dezvoltare pentru zona terenului. Datele sunt păstrate maximum 24 de luni și nu sunt transmise terților fără acordul meu.',
    consentLink: 'Politică de confidențialitate',

    summaryHeading: 'Rezumatul solicitării',
    summaryArea: 'Suprafață',
    summaryLocation: 'Localizare',
    summaryMethodDraw: 'Contur desenat pe hartă',
    summaryMethodManual: 'Suprafață declarată manual',
    summaryFiles: 'Documente',
    summaryFilesCount: (n: number) =>
      n === 1 ? '1 fișier atașat' : `${n} fișiere atașate`,
  },

  validation: {
    areaInvalid: 'Suprafața trebuie să fie un număr mai mare decât 0.',
    areaTooLarge: 'Suprafața pare prea mare. Verificați valoarea introdusă.',
    localitateRequired: 'Introduceți localitatea.',
    cutInvalid: 'CUT trebuie să fie un număr între 0 și 20.',
    potInvalid: 'POT trebuie să fie un număr între 0 și 100.',
    zonareAltRequired: 'Precizați utilizarea terenului.',
    fileType: 'Format neacceptat. Sunt acceptate doar PDF, JPG și PNG.',
    fileTooLarge: (maxMb: number) => `Fișierul depășește ${maxMb} MB.`,
    tooManyFiles: (max: number) => `Puteți atașa maximum ${max} documente suplimentare.`,
    nameRequired: 'Introduceți numele dumneavoastră.',
    emailRequired: 'Introduceți adresa de email.',
    emailInvalid: 'Adresa de email nu pare validă.',
    phoneRequired: 'Introduceți numărul de telefon.',
    phoneInvalid: 'Numărul de telefon nu pare valid.',
    // Actionable phrasing, not "trebuie să vă dați acordul".
    consentRequired: 'Bifați acordul pentru a putea trimite solicitarea.',
    fieldError: 'Eroare:',
  },

  submit: {
    errorHeading: 'Solicitarea nu a putut fi trimisă',
    errorGeneric:
      'A apărut o problemă la trimitere. Datele dumneavoastră au rămas în formular. Puteți încerca din nou.',
    errorNetwork:
      'Serverul nu a putut fi contactat. Verificați conexiunea la internet și încercați din nou.',
    errorValidation:
      'Serverul a respins câteva câmpuri. Verificați datele introduse și încercați din nou.',
    // "Vă rugăm" takes a subjunctive: "vă rugăm SĂ încercați", not "vă rugăm încercați".
    errorRateLimit:
      'S-au trimis prea multe solicitări de la această adresă. Vă rugăm să încercați din nou peste câteva minute.',
    errorTooLarge:
      'Fișierele atașate sunt prea mari. Eliminați sau comprimați documentele și încercați din nou.',
    retry: 'Încercați din nou',
  },

  success: {
    eyebrow: 'Solicitare înregistrată',
    /*
     * headline, body and signature MUST STAY IDENTICAL to their counterparts in
     * server/src/mail/templates.ts. The owner reads them on screen and then
     * again in their inbox; if the two drift, the email reads like a different
     * promise from the one they were just given. Change both in one commit.
     *
     * No "thank you". PRQ is giving the analysis away — thanking the owner for
     * accepting it inverts who is doing whom a favour.
     *
     * The body names the deliverable, states current value + potential, offers
     * the human process as proof, and repeats the no-exposure guarantee at the
     * moment of maximum receptivity.
     */
    headline: 'Am primit informațiile despre teren.',
    body: 'O echipă cu expertiză arhitecturală punctează parcela pe cinci criterii și estimează valoarea ei de astăzi. Punctajul de dezvoltare ajunge prin email în 3–5 zile lucrătoare. Terenul nu este listat și nu este promovat. Analiza este gratuită și o actualizăm anual.',
    signature: 'PRQ Partners',
    referenceLabel: 'Număr de referință',
    referenceHelp: 'Păstrați acest număr dacă doriți să ne scrieți despre solicitare.',
    emailedTo: (email: string) => `Confirmarea a fost trimisă la ${email}.`,

    /**
     * The deliverable. It lived on step 3, directly above the name and phone
     * fields, where it sat between the reader and the two inputs they were
     * most hesitant about — a promise offered as an argument for handing over
     * a phone number. Here it is read AFTER the commitment, so it confirms
     * what is coming rather than bargaining for it.
     *
     * The score comes first; price potential is conditional on qualifying,
     * which turns triage into a status mechanism rather than a rejection.
     */
    deliverableHeading: 'Ce primiți',
    deliverableItems: [
      'Punctajul de dezvoltare al terenului, de la 0 la 100, pe cinci criterii',
      'Estimarea valorii actuale a terenului',
      'Potențialul de preț, pentru terenurile care se califică',
      'Actualizare anuală, fără nicio solicitare din partea dumneavoastră',
    ],
  },

  /**
   * The cookie notice.
   *
   * Short and conventional on purpose. An earlier version named Meta and
   * explained the mechanism on the card itself; it read as a disclosure the
   * reader had to work through before using the page. The detail belongs in the
   * policy, which names the processor and lists exactly what is sent, and the
   * card links straight to it.
   *
   * Note what is NOT here: no "acceptați toate", no pre-ticked anything, and no
   * third styled button. The two options are given equal weight in the markup
   * and differ only in fill, because consent that is easier to give than to
   * refuse is not consent.
   *
   * One sentence. The "nu sunt necesare pentru funcționarea site-ului" line that
   * followed it was cut as redundant: a card offering "Refuz" as an equal option
   * already says refusing is free, and saying so in words as well reads like
   * reassurance nobody asked for.
   */
  consent: {
    regionLabel: 'Setări cookie-uri',
    body: 'Folosim cookie-uri pentru a măsura traficul și eficiența campaniilor.',
    accept: 'Accept',
    decline: 'Refuz',
    link: 'Detalii',
  },

  privacy: {
    title: 'Politică de confidențialitate',
    back: 'Înapoi la formular',
    lastUpdatedLabel: 'Ultima actualizare',
    lastUpdated: 'iulie 2026',
    sections: [
      {
        heading: 'Operatorul datelor',
        body: [
          /*
           * The legal entity, named, with its registered address and a working
           * contact address. Supplied by Mario-Dan on 2026-07-19; the bracketed
           * placeholders this replaced must never come back — this is the only
           * page that tells an owner who holds their documents and how to reach
           * them, which is what makes the consent on step 3 mean anything.
           *
           * "Prerequis", no acute accent: the registered company name.
           */
          'Operatorul datelor cu caracter personal colectate prin acest formular este Prerequis SRL, cu sediul în Str. Doctor Joseph Lister nr. 55, 050542 București, România, operând sub marca PRQ Partners. Pentru orice întrebare privind prelucrarea datelor, ne puteți scrie la contact@prqpartners.com.',
        ],
      },
      {
        heading: 'Ce date colectăm',
        body: [
          'Colectăm datele pe care le introduceți în formular: numele, adresa de email și numărul de telefon; localizarea și conturul parcelei desenate pe hartă sau suprafața și localitatea declarate de dumneavoastră; detaliile urbanistice ale terenului (CUT, POT, zonare, stare actuală); notele pe care ni le transmiteți; și documentele pe care le încărcați, inclusiv certificatul de urbanism.',
          'Colectăm de asemenea data și ora trimiterii și pagina de la care ați ajuns la formular.',
          'Dacă bifați acordul opțional de pe pagina cu analiza terenului, înregistrăm faptul, momentul și adresa IP de la care a fost dat.',
        ],
      },
      {
        heading: 'Scopul prelucrării',
        body: [
          /*
           * "exclusiv" is deliberately gone. The conditional contact below is a
           * second purpose, and a policy that claims a single exclusive purpose
           * while the consent checkbox names two is a policy that does not hold.
           *
           * Keep in step with step3.consentText — same promise, two places.
           */
          'Folosim aceste date pentru a analiza terenul descris, a pregăti documentația pe care v-o trimitem prin email și v-o punem la dispoziție pe o pagină web privată, a o actualiza periodic și a vă contacta în legătură cu această solicitare. Vă putem contacta și atunci când există interes concret de dezvoltare pentru zona terenului dumneavoastră, pentru a vă întreba dacă doriți o discuție; datele terenului nu sunt prezentate niciunui terț fără acordul dumneavoastră expres, exprimat la acel moment. Nu folosim datele pentru profilare automată și nu luăm decizii automate pe baza lor.',
        ],
      },
      {
        heading: 'Pagina cu analiza terenului',
        body: [
          'Analiza finalizată v-o punem la dispoziție pe o pagină web dedicată, accesibilă printr-un link privat pe care vi-l trimitem pe email. Pagina cuprinde un rezumat al analizei, harta parcelei și raportul complet în format PDF. Linkul este privat și nu este indexat: numai cine îl deține poate deschide pagina. Pagina și raportul sunt păstrate pe aceeași perioadă ca restul datelor solicitării.',
        ],
      },
      {
        heading: 'Acordul pentru oportunități viitoare (opțional)',
        body: [
          'Pe pagina analizei vă putem cere, opțional, acordul de a vă contacta dacă apare o oportunitate concretă de dezvoltare pentru terenul dumneavoastră. Bifarea este opțională și nu condiționează în niciun fel accesul la analiză sau la raport. Dacă vă dați acordul, înregistrăm faptul, momentul și adresa IP de la care a fost dat, ca dovadă a consimțământului, conform art. 6 alin. (1) lit. (a) și art. 7 din Regulamentul (UE) 2016/679. Vă puteți retrage acordul oricând, direct de pe pagină sau scriindu-ne la adresa de contact; retragerea nu afectează prelucrările făcute anterior.',
        ],
      },
      {
        heading: 'Temeiul legal',
        body: [
          'Prelucrarea se face pe baza consimțământului dumneavoastră, exprimat prin bifarea căsuței din formular, conform art. 6 alin. (1) lit. (a) din Regulamentul (UE) 2016/679 (GDPR). Vă puteți retrage consimțământul oricând, scriindu-ne la adresa de contact.',
        ],
      },
      {
        heading: 'Perioada de păstrare',
        body: [
          'Păstrăm datele maximum 24 de luni de la data trimiterii solicitării, după care le ștergem. Dacă ne cereți ștergerea mai devreme, o facem fără întârziere nejustificată.',
        ],
      },
      {
        heading: 'Servicii tehnice ale unor terți',
        body: [
          // Processor disclosure: the basemap and the address search. Aligns
          // the policy with the app's real architecture.
          'Pentru afișarea hărții folosim serviciul Mapbox, iar pentru căutarea de localități folosim serviciile Photon (komoot) și Nominatim (OpenStreetMap), ambele construite pe datele OpenStreetMap. Când folosiți harta sau căutarea, adresa IP a dispozitivului dumneavoastră și textul căutat sunt transmise acestor servicii, conform politicilor lor de confidențialitate. Aceste servicii nu primesc datele din formular.',
          /*
           * The Meta pixel. Separated from the paragraph above on purpose: those
           * two are functionally necessary to draw a parcel and run without
           * being asked, this one is marketing measurement and runs only on
           * consent. Lumping them together would imply the same legal basis for
           * both, which is exactly the confusion the banner exists to avoid.
           *
           * Keep in step with lib/consent.ts and lib/meta.ts. If a new event is
           * added there, it is named here.
           */
          'Dacă v-ați exprimat acordul, încărcăm și instrumentul de măsurare al Meta Platforms Ireland Limited (Meta Pixel). Acesta transmite către Meta adresa IP a dispozitivului, informații despre browser și momentele în care deschideți formularul, localizați parcela și trimiteți solicitarea. Ne arată ce anunțuri aduc proprietari de teren pe această pagină.',
          /*
           * The second paragraph is the honest half: Meta's pixel reports more
           * than the four events we fire, because automatic configuration is
           * left on and it logs page interactions by itself. A test run produced
           * five `SubscribedButtonClick` entries alongside our four.
           *
           * What it does NOT do is read the form. Meta's Automatic Advanced
           * Matching — which hashes name, email and phone out of the inputs and
           * sends them to match a visit to a Meta account — is OFF on this
           * dataset, deliberately: at this volume it improves nothing anyone
           * could measure, and the cookie card does not ask for anything like
           * it. The last sentence below is only true while it stays off.
           *
           * If it is ever switched on, this paragraph AND the consent card both
           * have to say so. They are one decision.
           */
          'Instrumentul înregistrează automat și alte interacțiuni cu pagina, cum sunt apăsările de butoane. Nu citește însă datele pe care le introduceți în formular: numele, adresa de email și numărul de telefon nu sunt transmise către Meta. Fără acordul dumneavoastră instrumentul nu se încarcă și nu transmite nimic, iar acordul poate fi retras oricând din subsolul paginii.',
        ],
      },
      {
        heading: 'Transmiterea către terți',
        body: [
          'Nu transmitem datele din formular către terți fără acordul dumneavoastră explicit. Datele sunt stocate pe infrastructura controlată de PRQ Partners.',
        ],
      },
      {
        heading: 'Drepturile dumneavoastră',
        body: [
          'Aveți dreptul de acces la date, de rectificare, de ștergere, de restricționare a prelucrării, de portabilitate și de opoziție. Aveți de asemenea dreptul de a depune o plângere la Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal (ANSPDCP).',
        ],
      },
      {
        heading: 'Contact',
        body: [
          'Pentru exercitarea drepturilor de mai sus sau pentru orice întrebare privind prelucrarea datelor, ne puteți scrie la contact@prqpartners.com.',
        ],
      },
    ],
  },

  footer: {
    privacy: 'Politică de confidențialitate',
    /*
     * Withdrawal has to be as easy as consent, so it is a link in the same row
     * as the policy, not a line in the policy telling people to clear their
     * browser. Shown only once a decision exists — before that the card is
     * already on screen and a second entry point would just be noise.
     */
    consentReset: 'Setări de măsurare',
    rights: (year: number) => `© ${year} PRQ Partners`,
  },

  a11y: {
    skipToContent: 'Săriți la conținut',
    stepperLabel: 'Etapele solicitării',
    stepComplete: 'finalizat',
    stepCurrent: 'pasul curent',
    mapLabel: 'Hartă pentru desenarea parcelei',
  },
} as const;

/** The shape every locale must satisfy. Derived from Romanian, the reference. */
export type Dictionary = typeof ro;
